import { ApiBounds, ApiFile, ApiMessage, ApiReference, ContentPart } from "../shared/interfaces";
import { FileViewerArgs } from "../components/fileviewer/FileViewer";

const REFERENCE_PATTERN = /\{(\d+(?:,\s*\d+)*)}|\[(\d+(?:,\s*\d+)*)]/g;

/**
 * Parses references from input text and returns an array of reference objects.
 */
export function parseReferences(input: string): Array<{
    page: number;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}> {
    const pattern = /#REFERENCE\(([\d,.]+)\)/g;
    let match;
    const results = [];

    while ((match = pattern.exec(input))) {
        const values = match[1].split(",");
        for (let i = 0; i < values.length; i += 5) {
            // Check if there are enough numbers to form a complete reference
            if (i + 4 < values.length) {
                const page = parseInt(values[i]);
                const x1 = parseFloat(values[i + 1]) * 100;
                const y1 = parseFloat(values[i + 2]) * 100;
                const x2 = parseFloat(values[i + 3]) * 100;
                const y2 = parseFloat(values[i + 4]) * 100;

                if (x1 <= 100.0 && y1 <= 100.0 && x2 <= 100.0 && y2 <= 100.0) {
                    results.push({ page, x1, y1, x2, y2 });
                }
            }
        }
    }

    return results;
}

/**
 * Converts numeric references in brackets within input text into Markdown links.
 */
export function convertReferencesToMarkdown(input: string): string {
    const parts = convertReferencesToParts(input);

    return parts
        .map((part, index) => {
            const markdown = part.isReference
                ? `[${part.rawContent}](#REFERENCE)`
                : part.rawContent;

            // If the current and next parts are a reference add a space
            if (part.isReference && index + 1 < parts.length) {
                if (parts[index + 1].isReference) {
                    return markdown + " ";
                }
            }

            return markdown;
        })
        .join("");
}

/**
 * Converts numeric references in brackets into structured content parts.
 */
export function convertReferencesToParts(input: string): ContentPart[] {
    const parts: ContentPart[] = [];
    let lastIndex = 0;

    // Use regex to find bracketed references like [1234, 5678] in the input text.
    input.replace(REFERENCE_PATTERN, (match, p1, p2, offset) => {
        // Add any text between the last match and the current match as a plain text part.
        if (offset > lastIndex) {
            parts.push({
                isReference: false,
                rawContent: input.slice(lastIndex, offset),
            });
        }

        // Split the matched reference by commas, handling each number as a separate reference part.
        const references = (p1! || p2!).split(/\s*,\s*/);
        references.forEach((num: string) => {
            parts.push({ isReference: true, rawContent: num });
        });

        // Update lastIndex to the end of the current match for the next iteration.
        lastIndex = offset + match.length;
        return match;
    });

    // Add any remaining plain text after the last match to the parts array.
    if (lastIndex < input.length) {
        parts.push({ isReference: false, rawContent: input.slice(lastIndex) });
    }

    return parts;
}

/**
 * Helper class for processing references
 */
export class ReferenceProcessor {
    private referenceMap: Map<string, ApiReference>;
    private readonly files: ApiFile[];

    constructor(message: ApiMessage) {
        this.referenceMap = new Map<string, ApiReference>(Object.entries(message.references ?? {}));
        this.files = message.files;
    }

    /**
     * Creates a long description of a reference.
     */
    private getDescription(referenceKey: string): string {
        const reference = this.referenceMap.get(referenceKey)!;
        const page = Array.from(Object.values(reference.bounds)).map((b) => b.page)[0] + 1;

        const firstPart = `${reference.sectionPath[0]} ${reference.sectionPath[1]}`;
        const remainingParts = reference.sectionPath.slice(2).join(" | ");
        const section = `${firstPart} | ${remainingParts}`;
        const file = this.getFileForReferenceKeyOrThrow(referenceKey);
        return `${section}\nPage ${page} in ${file.fileName}`;
    }

    /**
     * Creates file viewer highlight areas for a given reference.
     */
    private getHighlightAreas(referenceKey: string): ApiBounds[] {
        const reference = this.referenceMap.get(referenceKey)!;
        return Array.from(Object.values(reference!.bounds));
    }

    /**
     * Checks if the file viewer is showing the provided reference.
     */
    private isReferenceOpen(
        referenceKey: string,
        currentFileViewerFile: FileViewerArgs | null,
    ): boolean {
        if (!this.isReferenceValid(referenceKey) || !currentFileViewerFile) {
            return false;
        }

        const referencedFile = this.getFileForReferenceKeyOrThrow(referenceKey);
        const highlightAreas = this.getHighlightAreas(referenceKey);

        return (
            currentFileViewerFile.file.id === referencedFile.id &&
            JSON.stringify(currentFileViewerFile.referenceHighlights) ===
                JSON.stringify(highlightAreas)
        );
    }

    private ensureReferenceValidOrThrow(referenceKey: string) {
        if (!this.isReferenceValid(referenceKey)) {
            throw new Error("Key is not valid " + referenceKey);
        }
    }

    private getFileForReferenceKeyOrThrow(referenceKey: string): ApiFile {
        const reference = this.referenceMap.get(referenceKey);
        const file = this.files.find((f) => f.id === reference?.fileId);
        if (!file) {
            throw new Error("Unknown file for reference " + referenceKey);
        }

        return file;
    }

    /**
     * Checks if the reference is valid.
     *
     * A reference is valid if:
     * 1) It exists in the message `references` map AND
     * 2) The file it references exists in the message files `property`
     */
    public isReferenceValid(referenceKey: string): boolean {
        return (
            this.files.length > 0 &&
            this.referenceMap.size > 0 &&
            this.referenceMap.has(referenceKey)
        );
    }

    /**
     * Creates human-friendly text for a given key.
     */
    public getText(referenceKey: string): string {
        this.ensureReferenceValidOrThrow(referenceKey);

        const reference = this.referenceMap.get(referenceKey)!;

        return `${reference.sectionPath[1]} | ${reference.sectionPath[2]}`;
    }

    /**
     * Get detailed information for a reference.
     */
    public getInformationForReference(
        referenceKey: string,
        currentFileViewerFile: FileViewerArgs | null,
    ): {
        text: string;
        description: string;
        highlightAreas: ApiBounds[];
        isReferenceOpen: boolean;
        fileViewerArgs: FileViewerArgs;
    } {
        this.ensureReferenceValidOrThrow(referenceKey);

        const file = this.getFileForReferenceKeyOrThrow(referenceKey);

        return {
            text: this.getText(referenceKey),
            description: this.getDescription(referenceKey),
            highlightAreas: this.getHighlightAreas(referenceKey),
            isReferenceOpen: this.isReferenceOpen(referenceKey, currentFileViewerFile),
            fileViewerArgs: {
                file: file!,
                referenceHighlights: this.getHighlightAreas(referenceKey),
            },
        };
    }
}
